import React, { useState } from 'react';
import PhotoCapturer from './PhotoCapturer';
import LocalPhotoCapturer from './LocalPhotoCapturer';

type CaptureCallback = (identifier: string, base64Image: string) => void;

/**
 * A hook designed to ease usage of the PhotoCapturer component. Ideally, you should always
 * use this hook instead of using the component directly to simplify lifecycle management.
 *
 * @param captureCallback - Callback that will be invoked once an image is captured.
 */
export default function usePhotoCapturer(captureCallback: CaptureCallback): {
  cameraView: JSX.Element;
  requestImage: (imageIdentifier: string) => void;
  requestLocalImage: (imageIdentifier: string) => void;
} {
  const [cameraView, updateCameraView] = useState<JSX.Element>(<div />);

  /**
   * The photo was successfully captured, hide the camera and invoke the callback.
   *
   * @param identifier - Unique identifier for the image
   * @param base64Image - The image's base64 representation
   */
  function imageCaptured(identifier: string, base64Image: string): void {
    updateCameraView(<div />);
    captureCallback(identifier, base64Image);
  }

  /**
   * The capture was cancelled, hide the camera
   */
  function captureCancelled(): void {
    updateCameraView(<div />);
  }

  /**
   * Displays the camera and allows the user to capture an image.
   *
   * @param imageIdentifier - The unique identifier that we will use to identify the image
   */
  function requestImage(imageIdentifier: string): void {
    updateCameraView(
      <PhotoCapturer
        imageIdentifier={imageIdentifier}
        onCaptureCancelled={captureCancelled}
        onImageCapture={imageCaptured}
      />,
    );
  }

  function requestLocalImage(imageIdentifier: string): void {
    updateCameraView(() => (
      <LocalPhotoCapturer
        imageIdentifier={imageIdentifier}
        onImageCaptured={imageCaptured}
        onCaptureCancelled={captureCancelled}
      />
    ));
  }

  return {
    cameraView,
    requestImage,
    requestLocalImage,
  };
}
